import React from "react";
import Image from "next/image";
import logo from "../assets/logo.png";
import { useRouter } from "next/router";
import Link from "next/link";
import { useT } from "@jammable/ui-core/src/i18n";
import LanguageSwitcher from "./LanguageSwitcher";

// The vanity invite. Note other invite codes exist in the codebase
// (discord.gg/pBnz7CUwNW in ios.js, discord.gg/uURqJ9w4c3 in IosBETA) - this
// is the canonical one, already used by app.js and link/discord.js.
const DISCORD_URL = "https://discord.gg/jammable";

// Titles are message keys. "Jammable" is the brand and stays as it is.
const COLUMNS = [
  {
    title: "nav.footer.colCreate",
    items: [
      { title: "nav.footer.aiCover", url: "/ai-cover" },
      { title: "nav.footer.voiceChanger", url: "/ai-voice-changer" },
      { title: "nav.footer.voiceLibrary", url: "/models" },
      { title: "nav.footer.train", url: "/train" },
      { title: "nav.footer.weights", url: "/weights-gg-alternative" },
    ],
  },
  {
    title: "Jammable",
    items: [
      { title: "nav.pricing", url: "/pricing" },
      { title: "nav.library", url: "/library" },
      { title: "nav.footer.settings", url: "/settings" },
      { title: "nav.footer.earn", url: "/earn" },
      { title: "nav.api", url: "/developers" },
      { title: "nav.footer.mcp", url: "/mcp" },
    ],
  },
  {
    title: "nav.footer.colAccount",
    items: [
      { title: "nav.login", url: "/login" },
      { title: "nav.signup", url: "/signup" },
      { title: "nav.footer.forgot", url: "/forgotpassword" },
    ],
  },
  {
    title: "nav.footer.colLegal",
    items: [
      { title: "nav.footer.terms", url: "/terms" },
      { title: "nav.footer.privacy", url: "/privacy" },
      { title: "nav.footer.dmca", url: "/dmca-policy" },
      { title: "nav.footer.refunds", url: "/refunds" },
    ],
  },
];

const footerVisible = [
  "/",
  "/settings",
  "/help",
  "/privacy",
  "/terms",
  "/[model]",
  "/new/[model]",
  "/models",
  "/ai-cover",
  "/ai-voice-changer",
  "/weights-gg-alternative",
  "/developers",
  "/mcp",
  "/trainmodel",
  "/refunds",
  "/tiktokchallenge",
  "/tiktokchallenge/terms",
  "/community",
  "/custommodels",
  "/affiliate/terms",
  "/earn",
  "/tools",
  "/tools/[tool]",
  "/pricing",
  "/community/[id]",
  "/user/[id]",
  "/dmca-policy",
  "/presets/[effect]",
  "/shop",
  "/train",
  "/collection/[collectionid]",
  "/@/[id]",
  "/library",
  "/dashboard",
  "/dashboard/[songid]",
  // Localised landing pages; the footer carries the language switcher.
  "/es",
  "/es/[slug]",
  "/pt",
  "/pt/[slug]",
];

const PageFooter = () => {
  const router = useRouter();
  const t = useT();
  if (!footerVisible.includes(router.pathname)) return null;

  return (
    <footer className="tw-mt-16 tw-bg-jam-lav tw-pb-28 md:tw-pb-0">
      <div className="tw-mx-auto tw-grid tw-max-w-[1280px] tw-grid-cols-2 tw-gap-8 tw-px-4 tw-py-12 md:tw-grid-cols-[1.4fr_1fr_1fr_1fr_1fr] md:tw-px-8">
        <div className="tw-col-span-2 tw-flex tw-flex-col tw-gap-3.5 md:tw-col-span-1">
          <Link
            href="/"
            className="tw-flex tw-items-center tw-gap-2.5 tw-text-jam-ink tw-no-underline"
          >
            <Image src={logo} width={32} height={32} alt="Jammable" />
            <span className="jam-display tw-text-[22px] tw-font-extrabold">
              Jammable
            </span>
          </Link>
          <p className="tw-m-0 tw-max-w-[280px] tw-text-sm tw-font-semibold tw-text-jam-muted">
            {t("nav.footer.tagline")}
          </p>
          <a
            href={DISCORD_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="tw-self-start tw-rounded-full tw-bg-[#5865F2] tw-px-4 tw-py-2.5 tw-text-[13px] tw-font-extrabold tw-text-white tw-no-underline"
          >
            {t("nav.discordJoin")}
          </a>
          <LanguageSwitcher />
        </div>
        {COLUMNS.map((col) => (
          <div key={col.title} className="tw-flex tw-flex-col tw-gap-2.5">
            <span className="tw-text-xs tw-font-extrabold tw-uppercase tw-tracking-wider tw-text-jam-muted">
              {col.title === "Jammable" ? col.title : t(col.title)}
            </span>
            {col.items.map((item) => (
              <Link
                key={item.url}
                href={item.url}
                className="tw-text-sm tw-font-bold tw-text-jam-ink tw-no-underline hover:tw-text-jam-purple"
              >
                {t(item.title)}
              </Link>
            ))}
          </div>
        ))}
      </div>
      <div className="tw-mx-auto tw-flex tw-max-w-[1280px] tw-flex-col tw-gap-1 tw-px-4 tw-pb-8 tw-text-[13px] tw-font-semibold tw-text-jam-muted md:tw-px-8">
        <span>{`© ${new Date().getFullYear()} Jammable Limited`}</span>
        <span>
          {t("nav.footer.pitchmeldBefore")}{" "}
          <a
            href="https://pitchmeld.ing/"
            target="_blank"
            rel="noopener noreferrer"
            className="tw-text-jam-muted tw-underline"
          >
            {t("nav.footer.pitchmeldLink")}
          </a>{" "}
          {t("nav.footer.pitchmeldAfter")}
        </span>
      </div>
    </footer>
  );
};

export default PageFooter;
